import { UseMutationResult, useMutation, UseQueryResult, useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  AdjustSkuStockInput,
  AdjustSkuStockResponse,
  BatchToggleSkuStockInput,
  BatchToggleSkuStockResponse,
  CreateProductInput,
  CreateProductResponse,
  DeleteProductResponse,
  GetDataSkuListResponse,
  GetInventoryByFactoryIdResponse,
  GetProductByIdResponse,
  GetProductListInput,
  GetProductListResponse,
  IncreaseProductQtyInboundInput,
  IncreaseProductQtyInboundResponse,
  ListSkuStockTransactionsInput,
  ListSkuStockTransactionsResponse,
  UpdateProductInput,
  UpdateProductStatusInput,
  UpdateStatusProductResponse,
  UpdateProductSkuResponse,
  UpdateProductSkuDto,
  ToggleSkuStockInput,
  ToggleSkuStockResponse,
  UpsertSafeStockInput,
  UpsertSafeStockResponse,
  DeleteSafeStockInput,
  DeleteSafeStockResponse,
  GetSafeStockResponse,
} from 'services/interface'
import { adjustSkuStock, batchToggleSkuStock, crateProduct, deleteProduct, getInventoryByFactoryId, getProductById, getProductList, getProductSkuList, increaseQtyInbound, listSkuStockTransactions, toggleSkuStock, updateProduct, updateStatusProduct, upsertSafeStock, deleteSafeStock, getSafeStock } from 'services/productApi'
import { deleteSku, updateProductSku } from 'services/skuApi'




export const useCreateProduct = (): UseMutationResult<
  CreateProductResponse,
  Error,
  CreateProductInput,
  unknown
> => {
  return useMutation<CreateProductResponse, Error, CreateProductInput, unknown>({
    mutationFn: (input: CreateProductInput) => crateProduct(input),
    onSuccess: () => {
      // Handle success
    },
  })
}

export const useGetProductList = (
  input: GetProductListInput
): UseQueryResult<GetProductListResponse, Error> => {
  return useQuery<GetProductListResponse, Error>({
    queryKey: ['getProductList', input.search, input.is_active, input.is_approve, input.is_show_app],
    queryFn: () => getProductList(input),
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useGetProductListInActive = (
  input: GetProductListInput
): UseQueryResult<GetProductListResponse, Error> => {
  return useQuery<GetProductListResponse, Error>({
    queryKey: ['getProductListInActive', input.is_active, input.is_approve, input.search],
    queryFn: () => getProductList(input),
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useGetProductListApprove = (
  input: GetProductListInput
): UseQueryResult<GetProductListResponse, Error> => {
  return useQuery<GetProductListResponse, Error>({
    queryKey: ['getProductListApprove', input.is_active, input.is_approve, input.search],
    queryFn: () => getProductList(input),
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useDeleteProduct = (): UseMutationResult<
  DeleteProductResponse,
  Error,
  string,
  unknown
> => {
  return useMutation<DeleteProductResponse, Error, string, unknown>({
    mutationFn: (id: string) => deleteProduct(id),
  })
}

export const useDeleteSku = (): UseMutationResult<void, Error, string, unknown> => {
  return useMutation<void, Error, string, unknown>({
    mutationFn: (skuId: string) => deleteSku(skuId),
  })
}

export const useGetProductById = (
  productId: string
): UseQueryResult<GetProductByIdResponse, Error> => {
  return useQuery<GetProductByIdResponse, Error>({
    queryKey: ['product', productId],
    queryFn: () => getProductById(productId),
    enabled: !!productId,
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useGetProductSkuList = (): UseQueryResult<GetDataSkuListResponse, Error> => {
  return useQuery<GetDataSkuListResponse, Error>({
    queryKey: ['skuList'],
    queryFn: () => getProductSkuList(),
    // staleTime: 1000 * 60 * 60 * 24,
  })
}

export const useUpdateProduct = (): UseMutationResult<
  CreateProductResponse,
  Error,
  UpdateProductInput,
  unknown
> => {
  return useMutation<CreateProductResponse, Error, UpdateProductInput, unknown>({
    mutationFn: (input: UpdateProductInput) => updateProduct(input),
    onSuccess: () => {
      // Handle success
    },
  })
}

export const useUpdateStatusProduct = (): UseMutationResult<
  UpdateStatusProductResponse,
  Error,
  UpdateProductStatusInput,
  unknown
> => {
  return useMutation<UpdateStatusProductResponse, Error, UpdateProductStatusInput, unknown>({
    mutationFn: (input: UpdateProductStatusInput) => updateStatusProduct(input),
    onSuccess: () => {
      // Handle success
    },
  })
}

export const useIncreaseQtyInbound = (): UseMutationResult<
  IncreaseProductQtyInboundResponse,
  Error,
  IncreaseProductQtyInboundInput,
  unknown
> => {
  return useMutation<IncreaseProductQtyInboundResponse, Error, IncreaseProductQtyInboundInput, unknown>({
    mutationFn: (input: IncreaseProductQtyInboundInput) => increaseQtyInbound(input),
    onSuccess: () => {
      // Handle success
    },
  })
}

export const useUpdateProductSku = (): UseMutationResult<
  UpdateProductSkuResponse,
  Error,
  UpdateProductSkuDto,
  unknown
> => {
  return useMutation<UpdateProductSkuResponse, Error, UpdateProductSkuDto, unknown>({
    mutationFn: (input: UpdateProductSkuDto) => updateProductSku(input),
    onSuccess: () => {
      // Handle success
    },
  })
}

export const useToggleSkuStock = (): UseMutationResult<
  ToggleSkuStockResponse,
  Error,
  ToggleSkuStockInput,
  unknown
> => {
  return useMutation<ToggleSkuStockResponse, Error, ToggleSkuStockInput, unknown>({
    mutationFn: (input: ToggleSkuStockInput) => toggleSkuStock(input),
  })
}

export const useBatchToggleSkuStock = (): UseMutationResult<
  BatchToggleSkuStockResponse,
  Error,
  BatchToggleSkuStockInput,
  unknown
> => {
  return useMutation<BatchToggleSkuStockResponse, Error, BatchToggleSkuStockInput, unknown>({
    mutationFn: (input: BatchToggleSkuStockInput) => batchToggleSkuStock(input),
  })
}

export const useAdjustSkuStock = (): UseMutationResult<
  AdjustSkuStockResponse,
  Error,
  AdjustSkuStockInput,
  unknown
> => {
  return useMutation<AdjustSkuStockResponse, Error, AdjustSkuStockInput, unknown>({
    mutationFn: (input: AdjustSkuStockInput) => adjustSkuStock(input),
  })
}

export const useSkuStockTransactions = (
  input: ListSkuStockTransactionsInput,
  enabled: boolean
): UseQueryResult<ListSkuStockTransactionsResponse, Error> => {
  return useQuery<ListSkuStockTransactionsResponse, Error>({
    queryKey: [
      'skuStockTransactions',
      input.product_id,
      input.sku_id,
      input.page ?? 1,
      input.limit ?? 20,
    ],
    queryFn: () => listSkuStockTransactions(input),
    enabled: enabled && !!input.product_id && !!input.sku_id,
  })
}

export const useUpsertSafeStock = (): UseMutationResult<
  UpsertSafeStockResponse,
  Error,
  UpsertSafeStockInput,
  unknown
> => {
  return useMutation<UpsertSafeStockResponse, Error, UpsertSafeStockInput, unknown>({
    mutationFn: (input: UpsertSafeStockInput) => upsertSafeStock(input),
  })
}

/**
 * product_stock ของ SKU เอง ไม่ว่าตอนนี้อยู่โหมดไหน — sku/list (useSkuStockPicker) ตัด SKU ที่
 * "เชื่อมกับสต็อกหลัก" ออก จึงใช้หาสต็อกเดิมตอนสลับกลับ "จัดการสต็อกแยก" ไม่ได้
 */
export const useSkuOwnStock = (
  input: DeleteSafeStockInput,
  enabled: boolean
): UseQueryResult<GetSafeStockResponse, Error> => {
  return useQuery<GetSafeStockResponse, Error>({
    queryKey: ['skuOwnStock', input.product_id, input.sku_id],
    queryFn: () => getSafeStock(input),
    enabled: enabled && !!input.product_id && !!input.sku_id,
  })
}

export const useDeleteSafeStock = (): UseMutationResult<
  DeleteSafeStockResponse,
  Error,
  DeleteSafeStockInput,
  unknown
> => {
  return useMutation<DeleteSafeStockResponse, Error, DeleteSafeStockInput, unknown>({
    mutationFn: (input: DeleteSafeStockInput) => deleteSafeStock(input),
  })
}

export const useGetInventoryByFactoryId = (
  factory_id: string,
  /** ถ้า false จะไม่เรียก API (เช่น ยังไม่เปิดแท็บคลัง) */
  enabled = true,
  /** เรียงตามสต็อก (ทำที่ backend) — ใช้ที่หน้าคลังสินค้า */
  sort?: { sort_by: string; sort_dir: string },
  /** คงข้อมูลเดิมไว้ระหว่างเปลี่ยน sort (เฉพาะหน้าคลังสินค้า) — หน้าอื่นไม่ต้องใช้ */
  keepPrevious = false
): UseQueryResult<GetInventoryByFactoryIdResponse, Error> => {
  return useQuery<GetInventoryByFactoryIdResponse, Error>({
    queryKey:
      sort?.sort_by && sort?.sort_dir
        ? ['inventoryByFactory', factory_id, sort.sort_by, sort.sort_dir]
        : ['inventoryByFactory', factory_id],
    queryFn: () => getInventoryByFactoryId(factory_id, sort),
    enabled: !!factory_id && enabled,
    /** เปลี่ยน sort (query key เปลี่ยน) → คงตารางเดิมไว้ระหว่างโหลด ไม่ให้ทั้งตาราง/หัวคอลัมน์หาย */
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
    /** ข้อมูลคลังเปลี่ยนหลังปรับสต็อกที่ grid — ไม่ stale ทันที ลด refetch ซ้ำเมื่อสลับแท็บ */
    staleTime: 30_000,
    /** โหลดข้อมูลใหม่ทุกครั้งที่กลับเข้าหน้า (component mount) */
    refetchOnMount: 'always',
  })
}
